/**
 * SettingsConfirmEmail — Iter 191t
 *
 * Landing page for the confirmation link sent by
 * POST /api/auth/change-email/request. Reads the `token` from the URL,
 * calls POST /api/auth/change-email/confirm, then redirects to /auth.
 *
 * This route is intentionally PUBLIC (no auth needed) — the token itself
 * is the proof of possession of the new email inbox.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { DashboardBackdrop, GlassCard } from '@/components/dashboard/dashboard-ui';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SettingsConfirmEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState('pending'); // pending | success | error
  const [message, setMessage] = useState('Confirming your new email address…');
  const [newEmail, setNewEmail] = useState('');
  const nav = useNavigate();

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No confirmation token found in the link.');
      return;
    }
    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/change-email/confirm`, { token });
        setStatus('success');
        setNewEmail(data?.new_email || '');
        setMessage(data?.message || 'Email updated.');
        setTimeout(() => nav('/auth'), 4000);
      } catch (e) {
        setStatus('error');
        setMessage(e?.response?.data?.detail || 'This link is invalid or has expired.');
      }
    })();
  }, [token, nav]);

  return (
    <div className="relative min-h-screen">
      <DashboardBackdrop />
      <div className="max-w-md mx-auto pt-24 px-6" data-testid="settings-confirm-email-page">
        <GlassCard glow className="p-8 text-center">
          {status === 'pending' && (
            <>
              <Loader2 size={36} className="text-[#A78BFA] animate-spin mx-auto mb-4" />
              <h1 className="text-xl font-bold text-white font-syne">Confirming…</h1>
              <p className="text-sm text-white/60 mt-2">{message}</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle2 size={44} className="text-emerald-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white font-syne">Email confirmed</h1>
              <p className="text-sm text-white/70 mt-3">
                {message} You&rsquo;ll now sign in with <strong className="text-white">{newEmail}</strong>.
              </p>
              <Link
                to="/auth"
                className="inline-block mt-6 px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-[0.18em] bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white"
                data-testid="confirm-email-signin"
              >
                Sign in
              </Link>
              <p className="text-[10.5px] text-white/40 mt-4">Redirecting in a moment…</p>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle size={44} className="text-red-400 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-white font-syne">Couldn&rsquo;t confirm</h1>
              <p className="text-sm text-white/70 mt-3">{message}</p>
              <Link
                to="/website-builder/settings/privacy"
                className="inline-block mt-6 px-6 py-2.5 rounded-xl font-bold text-xs uppercase tracking-[0.18em] bg-white/10 border border-white/20 text-white hover:bg-white/15"
                data-testid="confirm-email-retry"
              >
                Try again
              </Link>
            </>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
